import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import MobileCardList from '../components/common/MobileCardList';
import ResponsiveSelect from '../components/common/ResponsiveSelect';
import { useDashboardOverview } from '../hooks/useDashboard';
import { Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, PointElement, LineElement, Title, Tooltip, Legend, Filler);

export default function Dashboard() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useDashboardOverview();
  const [activityFilter, setActivityFilter] = useState<string>('All Events');

  const dashboardData = data?.data;

  // Daily Activity Trend Chart Data
  const dailyActivityTrendChartData = useMemo(() => {
    if (!dashboardData || !dashboardData.dailyActivityTrend || dashboardData.dailyActivityTrend.length === 0) return null;
    
    return {
      labels: dashboardData.dailyActivityTrend.map(d => {
        const date = new Date(d.date);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }),
      datasets: [
        {
          label: 'GRN',
          data: dashboardData.dailyActivityTrend.map(d => d.grnCount),
          borderColor: '#1e3a5f',
          backgroundColor: 'rgba(30, 58, 95, 0.12)',
          fill: true,
          tension: 0.3,
          pointRadius: 2,
          pointHoverRadius: 4,
          borderWidth: 2,
        },
        {
          label: 'Dispatch',
          data: dashboardData.dailyActivityTrend.map(d => d.dispatchCount),
          borderColor: '#d97706',
          backgroundColor: 'rgba(217, 119, 6, 0.1)',
          fill: true,
          tension: 0.3,
          pointRadius: 2,
          pointHoverRadius: 4,
          borderWidth: 2,
        },
        {
          label: 'Gate Entry',
          data: dashboardData.dailyActivityTrend.map(d => d.gateEntryCount),
          borderColor: '#516075',
          backgroundColor: 'rgba(81, 96, 117, 0.1)',
          fill: true,
          tension: 0.3,
          pointRadius: 2,
          pointHoverRadius: 4,
          borderWidth: 2,
        },
      ],
    };
  }, [dashboardData]);

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        align: 'end' as const,
        labels: { usePointStyle: true, padding: 15, font: { size: 11 } },
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#1f2937',
        bodyColor: '#6b7280',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        padding: 12,
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 11 } } },
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
        ticks: {
          font: { size: 11 },
          precision: 0,
        },
      },
    },
  };

  // Product Category Distribution Chart Data
  const productCategoryChartData = useMemo(() => {
    if (!dashboardData || !dashboardData.productCategoryDistribution || dashboardData.productCategoryDistribution.length === 0) return null;
    
    const colors = [
      'rgba(30, 58, 95, 0.9)',
      'rgba(217, 119, 6, 0.85)',
      'rgba(81, 96, 117, 0.85)',
      'rgba(10, 22, 40, 0.8)',
      'rgba(61, 107, 145, 0.85)',
      'rgba(180, 83, 9, 0.8)',
    ];

    return {
      labels: dashboardData.productCategoryDistribution.map(c => c.categoryName),
      datasets: [
        {
          label: 'Quantity',
          data: dashboardData.productCategoryDistribution.map(c => c.quantity),
          backgroundColor: colors.slice(0, dashboardData.productCategoryDistribution.length),
          borderColor: colors.slice(0, dashboardData.productCategoryDistribution.length).map(c => c.replace('0.8', '1')),
          borderWidth: 2,
        },
      ],
    };
  }, [dashboardData]);

  const doughnutChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom' as const,
        labels: {
          padding: 15,
          font: { size: 11 },
          generateLabels: function(chart: any) {
            const data = chart.data;
            if (data.labels.length && data.datasets.length) {
              return data.labels.map((label: string, i: number) => {
                const value = data.datasets[0].data[i];
                const total = data.datasets[0].data.reduce((a: number, b: number) => a + b, 0);
                const percentage = ((value / total) * 100).toFixed(1);
                return {
                  text: `${label}: ${value} (${percentage}%)`,
                  fillStyle: data.datasets[0].backgroundColor[i],
                  hidden: false,
                  index: i,
                };
              });
            }
            return [];
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#1f2937',
        bodyColor: '#6b7280',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        padding: 12,
        callbacks: {
          label: function(context: any) {
            const label = context.label || '';
            const value = context.parsed;
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value} pallets (${percentage}%)`;
          }
        }
      },
    },
  };

  const formatEventType = (type: string) => {
    return type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  };

  const getEventIcon = (type: string) => {
    if (type.includes('GRN')) return 'fa-box';
    if (type.includes('DISPATCH')) return 'fa-truck';
    if (type.includes('GATE')) return 'fa-door-open';
    return 'fa-circle';
  };

  // Filter recent activity based on selected filter
  const filteredActivity = useMemo(() => {
    if (!dashboardData?.recentActivity) return [];
    if (activityFilter === 'All Events') return dashboardData.recentActivity;
    
    return dashboardData.recentActivity.filter(activity => {
      const eventType = activity.eventType.toUpperCase();
      if (activityFilter === 'GRN') return eventType.includes('GRN');
      if (activityFilter === 'Dispatch') return eventType.includes('DISPATCH');
      if (activityFilter === 'Gate Entry') return eventType.includes('GATE');
      return true;
    });
  }, [dashboardData?.recentActivity, activityFilter]);

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-ink-200 border-t-brand-600 mx-auto mb-4"></div>
            <p className="text-sm text-ink-500 font-medium">Loading operations snapshot...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="flex items-center justify-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <i className="fas fa-exclamation-triangle text-4xl mb-4" style={{ color: '#ef4444' }}></i>
            <p style={{ color: 'var(--text-primary)' }}>Error loading dashboard</p>
            <p style={{ color: 'var(--text-secondary)' }} className="text-sm mt-2">{error instanceof Error ? error.message : 'Unknown error'}</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!dashboardData) {
    return (
      <Layout>
        <div className="flex items-center justify-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <i className="fas fa-inbox text-4xl mb-4" style={{ color: '#6b7280' }}></i>
            <p style={{ color: 'var(--text-primary)' }}>No dashboard data available</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div>
        <div className="page-header flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
          <div>
            <p className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-accent-700 mb-1">Operations</p>
            <h1 className="page-title">Command Dashboard</h1>
            <p className="page-subtitle">Capacity, gate flow, approvals, and inventory health at a glance</p>
          </div>
          <div className="flex items-center gap-2 text-xs font-semibold text-ink-500">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-white border border-ink-200">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Live
            </span>
          </div>
        </div>

        {/* KPI strip — enterprise joined metrics */}
        <div className="kpi-strip mb-5">
          <div className="kpi-cell">
            <p className="text-[10px] font-extrabold uppercase tracking-[0.12em] text-ink-400 mb-2">Rack Capacity</p>
            <p className="text-2xl font-extrabold text-ink-900 tabular-nums">
              {dashboardData?.kpis.warehouseCapacity.usedCapacity || 0}
              <span className="text-ink-400 text-sm font-bold"> / {dashboardData?.kpis.warehouseCapacity.totalCapacity || 0}</span>
            </p>
            <p className="text-xs font-semibold text-brand-600 mt-1">
              {dashboardData?.kpis.warehouseCapacity.utilizationPercentage.toFixed(1) || 0}% utilized
            </p>
          </div>
          <div className="kpi-cell">
            <p className="text-[10px] font-extrabold uppercase tracking-[0.12em] text-ink-400 mb-2">Ground Storage</p>
            <p className="text-2xl font-extrabold text-ink-900 tabular-nums">
              {dashboardData?.kpis.groundStorage?.totalPallets || 0}
            </p>
            <p className="text-xs font-semibold text-accent-700 mt-1">pallets in zones</p>
          </div>
          <div className="kpi-cell bg-brand-900 text-white">
            <p className="text-[10px] font-extrabold uppercase tracking-[0.12em] text-white/50 mb-2">Gate Pending</p>
            <p className="text-2xl font-extrabold tabular-nums">
              {(dashboardData?.kpis.todaysActivity.inwardPendingDock || 0) + 
               (dashboardData?.kpis.todaysActivity.outwardPendingDock || 0)}
            </p>
            <p className="text-xs font-semibold text-accent-300 mt-1">
              <button type="button" className="hover:underline" onClick={() => navigate('/inward')}>
                In {dashboardData?.kpis.todaysActivity.inwardPendingDock || 0}
              </button>
              <span className="mx-1.5 text-white/30">|</span>
              <button type="button" className="hover:underline" onClick={() => navigate('/outward')}>
                Out {dashboardData?.kpis.todaysActivity.outwardPendingDock || 0}
              </button>
            </p>
          </div>
          <div className="kpi-cell cursor-pointer hover:bg-ink-50" onClick={() => navigate('/inventory/pending')}>
            <p className="text-[10px] font-extrabold uppercase tracking-[0.12em] text-ink-400 mb-2">Approvals</p>
            <p className="text-2xl font-extrabold text-ink-900 tabular-nums">
              {(dashboardData?.kpis.todaysActivity.grnPendingApproval || 0) + 
               (dashboardData?.kpis.todaysActivity.dispatchPendingApproval || 0)}
            </p>
            <p className="text-xs font-semibold text-ink-500 mt-1">
              GRN {dashboardData?.kpis.todaysActivity.grnPendingApproval || 0} · Disp {dashboardData?.kpis.todaysActivity.dispatchPendingApproval || 0}
            </p>
          </div>
          <div className="kpi-cell cursor-pointer hover:bg-ink-50" onClick={() => navigate('/inventory')}>
            <p className="text-[10px] font-extrabold uppercase tracking-[0.12em] text-ink-400 mb-2">Inventory Health</p>
            <p className="text-2xl font-extrabold text-ink-900 tabular-nums">
              {dashboardData?.kpis.inventoryHealth.goodQuantity || 0}
            </p>
            <p className="text-xs font-semibold text-ink-500 mt-1">
              Dmg {dashboardData?.kpis.inventoryHealth.damagedQuantity || 0} · Client {dashboardData?.kpis.inventoryHealth.clientDamagedQuantity || 0}
            </p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-extrabold uppercase tracking-[0.1em] text-ink-500">Shortcuts</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2.5">
            {[
              { label: 'Client Management', icon: 'fa-user-plus', path: '/clients', tone: 'bg-brand-50 text-brand-700' },
              { label: 'Security Check', icon: 'fa-id-card', path: '/security-check', tone: 'bg-accent-50 text-accent-700' },
              { label: 'Inbound', icon: 'fa-truck-ramp-box', path: '/inward', tone: 'bg-emerald-50 text-emerald-700' },
              { label: 'Outbound', icon: 'fa-truck-fast', path: '/outward', tone: 'bg-red-50 text-red-700' },
              { label: 'Inventory', icon: 'fa-cubes', path: '/inventory', tone: 'bg-sky-50 text-sky-700' },
              { label: 'Reports', icon: 'fa-chart-column', path: '/reports', tone: 'bg-ink-100 text-ink-700' },
            ].map((action) => (
              <button
                key={action.path}
                type="button"
                className="quick-action-btn"
                onClick={() => navigate(action.path)}
              >
                <div className={`quick-action-icon ${action.tone}`}>
                  <i className={`fas ${action.icon}`}></i>
                </div>
                <span className="text-xs font-bold text-ink-800">{action.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
          <div className="dashboard-card">
            <div className="flex items-center justify-between mb-3 pb-3 border-b border-ink-100">
              <h3 className="text-sm font-extrabold text-ink-900 uppercase tracking-[0.06em]">Activity Trend · 7 Days</h3>
            </div>
            <div style={{ height: '320px' }}>
              {dailyActivityTrendChartData ? (
                <Line data={dailyActivityTrendChartData} options={lineChartOptions} />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-sm text-ink-500">No activity data available</p>
                </div>
              )}
            </div>
          </div>

          <div className="dashboard-card">
            <div className="flex items-center justify-between mb-3 pb-3 border-b border-ink-100">
              <h3 className="text-sm font-extrabold text-ink-900 uppercase tracking-[0.06em]">Product Categories</h3>
            </div>
            <div style={{ height: '320px' }}>
              {productCategoryChartData ? (
                <Doughnut data={productCategoryChartData} options={doughnutChartOptions} />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-sm text-ink-500">No category data</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Chart Section - Takes 2 columns */}
          <div className="lg:col-span-2 space-y-6">
            {/* Recent Activity Table */}
            <div className="dashboard-card transition-all duration-300 hover:shadow-xl" style={{ animation: 'fadeInUp 1s ease-out' }}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Recent Activity</h3>
                <ResponsiveSelect
                  value={activityFilter}
                  onChange={(val) => setActivityFilter(val)}
                  includeEmpty={false}
                  className="px-3 py-1 rounded-lg text-sm border border-gray-200"
                  options={[
                    { value: 'All Events', label: 'All Events' },
                    { value: 'GRN', label: 'GRN' },
                    { value: 'Dispatch', label: 'Dispatch' },
                    { value: 'Gate Entry', label: 'Gate Entry' },
                  ]}
                />
              </div>
              <MobileCardList
                columns={[
                  {
                    key: 'eventType',
                    label: 'Event',
                    render: (value) => (
                      <div className="flex items-center justify-end">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center mr-2" style={{ background: 'rgba(30, 58, 95, 0.1)' }}>
                          <i className={`fas ${getEventIcon(value)} text-sm`} style={{ color: '#1e3a5f' }}></i>
                        </div>
                        <span className="text-sm font-medium">{formatEventType(value)}</span>
                      </div>
                    ),
                  },
                  { key: 'referenceId', label: 'Reference' },
                  { key: 'userName', label: 'User' },
                  {
                    key: 'timestamp',
                    label: 'Time',
                    render: (value) => new Date(value).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }),
                  },
                ]}
                data={filteredActivity ? filteredActivity.slice(0, 8) : []}
                emptyMessage="No recent activity"
              />
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-brand-900">
                      <th className="text-left py-2.5 px-4 text-[11px] font-extrabold text-white/85 uppercase tracking-[0.08em]">Event</th>
                      <th className="text-left py-2.5 px-4 text-[11px] font-extrabold text-white/85 uppercase tracking-[0.08em]">Reference</th>
                      <th className="text-left py-2.5 px-4 text-[11px] font-extrabold text-white/85 uppercase tracking-[0.08em]">User</th>
                      <th className="text-left py-2.5 px-4 text-[11px] font-extrabold text-white/85 uppercase tracking-[0.08em]">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredActivity && filteredActivity.length > 0 ? (
                      filteredActivity.slice(0, 8).map((activity, idx) => (
                      <tr 
                        key={idx} 
                        className="group hover:bg-brand-50/50 transition-colors duration-200 border-b border-ink-100 last:border-b-0" 
                        style={{ 
                          borderBottom: '1px solid var(--border-color)',
                          animation: `fadeInUp ${0.3 + idx * 0.1}s ease-out`
                        }}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center">
                            <div className="w-8 h-8 rounded-full flex items-center justify-center mr-3" style={{ background: 'rgba(30, 58, 95, 0.1)' }}>
                              <i className={`fas ${getEventIcon(activity.eventType)} text-sm`} style={{ color: '#1e3a5f' }}></i>
                            </div>
                            <span className="text-sm font-medium group-hover:text-primary-600 transition-colors duration-200" style={{ color: 'var(--text-primary)' }}>
                              {formatEventType(activity.eventType)}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm" style={{ color: 'var(--text-primary)' }}>{activity.referenceId}</td>
                        <td className="py-3 px-4 text-sm" style={{ color: 'var(--text-secondary)' }}>{activity.userName}</td>
                        <td className="py-3 px-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
                          {new Date(activity.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </td>
                      </tr>
                    ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="py-8 text-center text-sm" style={{ color: 'var(--text-secondary)' }}>
                          No recent activity
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="space-y-6">
            {/* Zone Storage Distribution */}
            <div className="dashboard-card transition-all duration-300 hover:shadow-xl" style={{ animation: 'fadeInRight 0.9s ease-out' }}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                  <i className="fas fa-layer-group mr-2" style={{ color: '#1e3a5f' }}></i>
                  Zone Storage
                </h3>
                <span className="text-xs px-2 py-1 rounded-full" style={{ background: 'rgba(30, 58, 95, 0.1)', color: '#1e3a5f' }}>
                  By Zone
                </span>
              </div>
              <div className="space-y-4 scrollbar-hide" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {dashboardData?.storageDistribution && dashboardData.storageDistribution.length > 0 ? (
                  dashboardData.storageDistribution.map((zone, idx) => (
                    <div key={idx} className="transition-all duration-300 hover:scale-105">
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center mr-2" style={{ background: 'rgba(30, 58, 95, 0.1)' }}>
                            <i className="fas fa-warehouse text-xs" style={{ color: '#1e3a5f' }}></i>
                          </div>
                          <div>
                            <span className="text-sm font-semibold block" style={{ color: 'var(--text-primary)' }}>{zone.zoneName}</span>
                            <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{zone.palletCount} pallets</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4">
                    <i className="fas fa-inbox text-2xl mb-2" style={{ color: 'var(--text-secondary)', opacity: 0.5 }}></i>
                    <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>No zone storage data</p>
                  </div>
                )}
              </div>
            </div>

            {/* Rack Storage Distribution */}
            <div className="dashboard-card transition-all duration-300 hover:shadow-xl" style={{ animation: 'fadeInRight 1s ease-out' }}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                  <i className="fas fa-th mr-2" style={{ color: '#10b981' }}></i>
                  Rack Storage
                </h3>
                <span className="text-xs px-2 py-1 rounded-full" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
                  By Rack
                </span>
              </div>
              <div className="space-y-4 scrollbar-hide" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {dashboardData?.rackStorageDistribution && dashboardData.rackStorageDistribution.length > 0 ? (
                  dashboardData.rackStorageDistribution.map((rack, idx) => (
                    <div key={idx} className="transition-all duration-300 hover:scale-105">
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center mr-2" style={{ background: 'rgba(16, 185, 129, 0.1)' }}>
                            <i className="fas fa-grip-vertical text-xs" style={{ color: '#10b981' }}></i>
                          </div>
                          <div>
                            <span className="text-sm font-semibold block" style={{ color: 'var(--text-primary)' }}>
                              {rack.rackCode} - {rack.rackName}
                            </span>
                            <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{rack.palletCount} pallets</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4">
                    <i className="fas fa-inbox text-2xl mb-2" style={{ color: 'var(--text-secondary)', opacity: 0.5 }}></i>
                    <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>No rack storage data</p>
                  </div>
                )}
              </div>
            </div>

            {/* Capacity by Client */}
            <div className="dashboard-card transition-all duration-300 hover:shadow-xl" style={{ animation: 'fadeInRight 1.1s ease-out' }}>
              <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Capacity by Client</h3>
              <div className="space-y-4">
                {dashboardData?.capacityByClient && dashboardData.capacityByClient.length > 0 ? (
                  dashboardData.capacityByClient.map((client, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center justify-between p-3 rounded-lg transition-all duration-300 hover:scale-105 cursor-pointer" 
                    style={{ 
                      background: 'var(--bg-secondary)',
                      animation: `fadeInUp ${0.5 + idx * 0.1}s ease-out`
                    }}
                  >
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center mr-3" style={{ background: 'rgba(30, 58, 95, 0.1)' }}>
                        <i className="fas fa-user text-sm" style={{ color: '#1e3a5f' }}></i>
                      </div>
                      <div>
                        <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{client.clientName}</div>
                        <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>{client.palletCount} pallets</div>
                      </div>
                    </div>
                    <div className="text-sm font-semibold" style={{ color: '#1e3a5f' }}>{client.percentage.toFixed(2)}%</div>
                  </div>
                ))
                ) : (
                  <p className="text-sm text-center py-4" style={{ color: 'var(--text-secondary)' }}>No client data</p>
                )}
              </div>
            </div>

            {/* Active Operations */}
            <div className="dashboard-card transition-all duration-300 hover:shadow-xl" style={{ animation: 'fadeInRight 1.2s ease-out' }}>
              <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Active Operations</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-lg" style={{ background: 'rgba(16, 185, 129, 0.1)' }}>
                  <div className="flex items-center">
                    <i className="fas fa-arrow-down mr-3" style={{ color: '#10b981' }}></i>
                    <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Inbound at Dock</span>
                  </div>
                  <span className="text-lg font-bold" style={{ color: '#10b981' }}>{dashboardData?.kpis.activeOperations.inboundAtDock || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg" style={{ background: 'rgba(239, 68, 68, 0.1)' }}>
                  <div className="flex items-center">
                    <i className="fas fa-arrow-up mr-3" style={{ color: '#ef4444' }}></i>
                    <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Outbound at Dock</span>
                  </div>
                  <span className="text-lg font-bold" style={{ color: '#ef4444' }}>{dashboardData?.kpis.activeOperations.outboundAtDock || 0}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
